import { Controller } from '@hotwired/stimulus'

// 口コミカード（review_card）のクランプ制御。PC/SP共用のため common/ に配置する
// （site_desktop.js / site_mobile.js の require.context が common/component_controller を登録する。
// 識別子の前提は common/component.rb の stimulus_controller コメント参照）。
//
// - pickup:  本文が3行を超える場合のみ3行クランプ＋「続きを見る」リンク（口コミ一覧へ遷移）を表示
// - pickup2: 本文が3行を超える場合のみ3行クランプ＋「もっと見る」ボタンでその場展開（expandable-value=true）
//
// 状態クラスはビュー側が data-*-class（Classes API）で渡し、JSはリテラルのクラス名・セレクタを持たない
const CLAMP_LINES = 3

export default class extends Controller {
  static targets = ['text', 'readMore', 'toggleButton', 'toggleLabel']
  static classes = ['clamped', 'expanded', 'visibleButton']
  static values = { expandable: Boolean }

  connect () {
    if (!this.hasTextTarget) { return }
    const text = this.textTarget
    // 判定してからクランプする（fail-open）。先にクランプすると、Webフォントロード前の
    // 過小測定で「本文が切れているのに展開手段が無い」状態に倒れるため。
    // なおCSS既定の5行クランプ（styles/card.scss）は残るため、fail-openが完全なのは
    // 既定クランプ内に収まる本文まで（5行超で誤判定した場合は5行で切れたままになる）
    const isLong = this.measureFullHeight(text) > this.resolveLineHeight(text) * CLAMP_LINES + 1
    if (!isLong) { return }

    // 展開手段（ボタン/リンク）の存在を確認してからクランプする。
    // target欠落時に先へクランプすると「切り詰めたのに展開できない」状態へ静かに倒れるため
    if (this.expandableValue) {
      // ラベルまで確認する（旧実装の `if (!text || !button || !label) return` と同強度）。
      // ラベルが無いと展開はできるが表示が「もっと見る」のまま固まり、エラーも出ず気づけない
      if (!this.hasToggleButtonTarget || !this.hasToggleLabelTarget) { return }
      text.classList.add(this.clampedClass)
      this.toggleButtonTarget.classList.add(this.visibleButtonClass)
    } else {
      if (!this.hasReadMoreTarget) { return }
      text.classList.add(this.clampedClass)
      this.readMoreTarget.hidden = false
    }
  }

  // pickup2「もっと見る/閉じる」のその場展開
  toggle (event) {
    event.preventDefault()
    // connect() と同様にtargetの存在を確認してから触る（target欠落時に例外で落とさない）
    if (!this.hasToggleButtonTarget || !this.hasTextTarget) { return }
    const button = this.toggleButtonTarget
    const isExpanded = button.getAttribute('aria-expanded') === 'true'
    button.setAttribute('aria-expanded', String(!isExpanded))
    this.textTarget.classList.toggle(this.clampedClass, isExpanded)
    this.textTarget.classList.toggle(this.expandedClass, !isExpanded)
    if (this.hasToggleLabelTarget) {
      this.toggleLabelTarget.textContent = isExpanded ? 'もっと見る' : '閉じる'
    }
  }

  // クランプ行数の基準となる1行の高さ。line-height: normal 等でlineHeightが数値にならない場合は
  // font-size×1.6（本文line-height相当）にフォールバックし、クランプ機構が静かに無効化されるのを防ぐ
  resolveLineHeight (text) {
    const style = getComputedStyle(text)
    return parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6
  }

  // クランプ判定用: クランプ系スタイルを一時解除して全文の高さを測る
  measureFullHeight (text) {
    const original = {
      display: text.style.display,
      lineClamp: text.style.lineClamp,
      webkitLineClamp: text.style.webkitLineClamp,
      webkitBoxOrient: text.style.webkitBoxOrient,
      overflow: text.style.overflow,
    }
    text.style.display = 'block'
    text.style.lineClamp = 'unset'
    text.style.webkitLineClamp = 'unset'
    text.style.webkitBoxOrient = 'initial'
    text.style.overflow = 'visible'
    const fullHeight = text.scrollHeight
    Object.assign(text.style, original)
    return fullHeight
  }
}
